import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Globe, Lock, MoreHorizontal, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import type { StatusPage } from "@shared/types";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import * as api from "@/lib/api";
import { queryKeys, useStatusPages } from "@/lib/queries";

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Something went wrong";
}

function isImageUrl(icon: string) {
  return /^https?:\/\//i.test(icon);
}

export default function StatusPagesPage() {
  const { data: pages, isLoading } = useStatusPages();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState<StatusPage | null>(null);
  const [busy, setBusy] = useState(false);

  async function deletePage() {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.deleteStatusPage(deleting.id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.statusPages });
      toast.success("Status page deleted");
      setDeleting(null);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-5xl mx-auto w-full">
      <PageHeader
        title="Status Pages"
        actions={<Button asChild><Link to="/status-pages/new"><Plus />New status page</Link></Button>}
      />
      {isLoading ? (
        <div className="space-y-4">{[0, 1, 2].map((item) => <Skeleton key={item} className="h-28 rounded-xl" />)}</div>
      ) : !pages?.length ? (
        <EmptyState
          icon={Globe}
          title="No status pages"
          description="Publish a branded view of the services your users rely on."
          action={<Button asChild><Link to="/status-pages/new"><Plus />New status page</Link></Button>}
        />
      ) : (
        <div className="space-y-4">
          {pages.map((page) => (
            <Card key={page.id}>
              <CardContent className="flex flex-wrap items-center gap-4 p-5">
                <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-muted text-2xl">
                  {page.icon ? (
                    isImageUrl(page.icon) ? <img src={page.icon} alt="" className="size-8 rounded object-cover" /> : <span aria-hidden>{page.icon}</span>
                  ) : <Globe className="size-5 text-muted-foreground" />}
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="truncate font-semibold">{page.title}</h2>
                  <a href={`/status/${page.slug}`} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                    /status/{page.slug}<ExternalLink className="size-3" />
                  </a>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {page.published ? <Badge className="border-status-up/30 bg-status-up/10 text-status-up">Published</Badge> : <Badge variant="secondary">Draft</Badge>}
                    {page.hasPassword && <Badge variant="outline"><Lock />Password</Badge>}
                    {page.customDomain && <Badge variant="outline"><Globe />{page.customDomain}</Badge>}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" aria-label={`Actions for ${page.title}`}><MoreHorizontal /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild><Link to={`/status-pages/${page.id}`}>Edit</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><a href={`/status/${page.slug}`} target="_blank" rel="noreferrer">Open <ExternalLink /></a></DropdownMenuItem>
                    <DropdownMenuItem variant="destructive" onClick={() => setDeleting(page)}>Delete</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <AlertDialog open={Boolean(deleting)} onOpenChange={(open) => { if (!open) setDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this status page?</AlertDialogTitle><AlertDialogDescription>This removes the public page, its sections, and its incidents. This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => void deletePage()} disabled={busy}>Delete page</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
